/**
 * RotationManager — patrones de rotación de turnos (art. 36 ET).
 *
 * Ciclos de pasos (turno × días, o descanso) y asignación anclada a fecha.
 * El tope de noches seguidas lo resuelve el backend por jurisdicción del
 * tenant (`legal_max_consecutive_nights`, null = esa jurisdicción no fija
 * tope): aquí sólo se AVISA con el dato que trae el convenio — nunca con el
 * 14 español y su artículo a fuego, que en un tenant andorrano o base UE
 * citaban una ley que no es la suya (ronda 4, r2b20[7]).
 */
import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from './AdminLayout';
import {
  getRotationPatterns,
  createRotationPattern,
  assignRotation,
  getRotationAssignments,
  deactivateRotationAssignment,
  getShiftTemplates,
  getUsers,
  getWorkPolicies,
  apiMessage,
  RotationPatternItem,
  RotationAssignmentItem,
  ShiftTemplateItem,
  AdminUser,
} from '../../services/apiClient';

interface StepDraft {
  shift_template_id: number | null;
  duration_days: number;
}

export function RotationManager() {
  const [patterns, setPatterns] = useState<RotationPatternItem[]>([]);
  const [templates, setTemplates] = useState<ShiftTemplateItem[]>([]);
  const [assignments, setAssignments] = useState<RotationAssignmentItem[]>([]);
  const [workers, setWorkers] = useState<AdminUser[]>([]);
  // Tope de noches seguidas de la jurisdicción del tenant (null = sin tope
  // legal): lo trae el convenio por defecto, el mismo dato que pinta la
  // pantalla de Convenio.
  const [legalMaxNights, setLegalMaxNights] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Alta de patrón
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [steps, setSteps] = useState<StepDraft[]>([
    { shift_template_id: null, duration_days: 7 },
  ]);

  // Asignación
  const [assignPattern, setAssignPattern] = useState<number | null>(null);
  const [assignUserId, setAssignUserId] = useState('');
  const [anchorDate, setAnchorDate] = useState('');
  const [voluntaryNight, setVoluntaryNight] = useState(false);

  const load = useCallback(async () => {
    const [p, t, a] = await Promise.all([
      getRotationPatterns(),
      getShiftTemplates(),
      getRotationAssignments(),
    ]);
    setPatterns(p);
    setTemplates(t);
    setAssignments(a);
    // La plantilla con nombres (`GET /users`) es sólo de ADMIN. Para un
    // MANAGER el selector queda vacío y el formulario lo dice — aceptar un
    // identificador tecleado a ciegas era justo el defecto.
    getUsers().then(setWorkers).catch(() => setWorkers([]));
    // Tope de noches por jurisdicción (best-effort: sin él, el aviso no
    // pinta cifras — nunca inventa la española).
    getWorkPolicies()
      .then((ps) => setLegalMaxNights(ps.find((p) => p.is_default)?.legal_max_consecutive_nights ?? null))
      .catch(() => setLegalMaxNights(null));
  }, []);

  useEffect(() => {
    load().catch(() => setError('Error cargando patrones'));
  }, [load]);

  const templateName = (id: number | null) =>
    id === null ? 'Descanso' : templates.find((t) => t.id === id)?.name ?? `#${id}`;

  const workerName = (id: number) => {
    const w = workers.find((u) => u.id === id);
    return w ? `${w.first_name} ${w.last_name}`.trim() : `Usuario #${id}`;
  };

  const createPattern = async () => {
    setError(null);
    setNotice(null);
    try {
      await createRotationPattern({ name, steps });
      setShowForm(false);
      setName('');
      setSteps([{ shift_template_id: null, duration_days: 7 }]);
      await load();
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo crear el patrón'));
    }
  };

  const assign = async () => {
    if (!assignPattern) return;
    setError(null);
    setNotice(null);
    const pattern = patterns.find((p) => p.id === assignPattern);
    try {
      await assignRotation(assignPattern, {
        user_id: Number(assignUserId),
        anchor_date: anchorDate,
        voluntary_night: voluntaryNight,
      });
      setNotice(
        `${pattern?.name ?? 'Rotación'} asignada a ${workerName(Number(assignUserId))}` +
          ` (día 0 del ciclo = ${anchorDate})`,
      );
      setAssignPattern(null);
      setAssignUserId('');
      setAnchorDate('');
      setVoluntaryNight(false);
      await load();
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo asignar la rotación'));
    }
  };

  const deactivate = async (a: RotationAssignmentItem) => {
    setError(null);
    setNotice(null);
    try {
      await deactivateRotationAssignment(a.id);
      setNotice(`Rotación retirada a ${a.user_name ?? `Usuario #${a.user_id}`}`);
      await load();
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo retirar la asignación'));
    }
  };

  return (
    <AdminLayout title="Rotaciones">
      <div className="p-4 space-y-3">
        {error && (
          <div className="bg-danger-soft text-danger rounded-lg p-3 text-sm">
            {error}
          </div>
        )}
        {notice && (
          <div className="bg-brand-soft text-brand rounded-lg p-3 text-sm">
            {notice}
          </div>
        )}

        {assignments.length > 0 && (
          <div className="bg-surface rounded-xl p-4 shadow-sm">
            <h3 className="font-medium text-ink mb-2 text-sm">
              Asignaciones activas
            </h3>
            <div className="space-y-1">
              {assignments.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2">
                  <p className="text-xs text-ink-2">
                    <span className="text-ink font-medium">
                      {a.user_name ?? `Usuario #${a.user_id}`}
                    </span>{' '}
                    · {a.pattern_name ?? `Patrón #${a.pattern_id}`} · desde{' '}
                    {a.anchor_date}
                    {a.voluntary_night && ' · adscripción voluntaria a noche'}
                  </p>
                  <button
                    onClick={() => deactivate(a)}
                    className="text-danger text-xs shrink-0"
                  >
                    Desasignar
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={() => setShowForm(!showForm)}
          className="w-full py-3 bg-brand text-white rounded-xl font-medium hover:bg-brand-2"
        >
          {showForm ? 'Cerrar' : '+ Nuevo patrón de rotación'}
        </button>

        {showForm && (
          <div className="bg-surface rounded-xl p-4 shadow-sm space-y-3">
            <input
              placeholder="Nombre (p. ej. M/T/N semanal)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border-line-strong text-sm"
            />
            {steps.map((step, i) => (
              <div key={i} className="flex gap-2 items-center">
                <span className="text-xs text-ink-3 w-6">{i + 1}.</span>
                <select
                  value={step.shift_template_id ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : Number(e.target.value);
                    setSteps(steps.map((s, j) => (j === i ? { ...s, shift_template_id: v } : s)));
                  }}
                  className="flex-1 rounded-lg border-line-strong text-sm"
                >
                  <option value="">Descanso</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.start_time}–{t.end_time})
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={1}
                  value={step.duration_days}
                  onChange={(e) =>
                    setSteps(steps.map((s, j) =>
                      j === i ? { ...s, duration_days: Number(e.target.value) } : s
                    ))
                  }
                  className="w-20 rounded-lg border-line-strong text-sm"
                />
                <span className="text-xs text-ink-3">días</span>
                <button
                  onClick={() => setSteps(steps.filter((_, j) => j !== i))}
                  className="text-danger text-sm"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              onClick={() => setSteps([...steps, { shift_template_id: null, duration_days: 7 }])}
              className="text-sm text-brand"
            >
              + Añadir paso
            </button>
            <button
              onClick={createPattern}
              disabled={!name || steps.length === 0}
              className="w-full py-2 bg-brand text-white rounded-lg text-sm hover:bg-brand-2 disabled:opacity-40"
            >
              Crear patrón
            </button>
          </div>
        )}

        {patterns.map((p) => (
          <div key={p.id} className="bg-surface rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-ink">{p.name}</p>
                <p className="text-xs text-ink-2">
                  Ciclo de {p.cycle_days} días ·{' '}
                  {p.steps.map((s) => `${templateName(s.shift_template_id)}×${s.duration_days}`).join(' → ')}
                </p>
                {legalMaxNights !== null && p.max_night_run_days > legalMaxNights && (
                  <p className="text-xs text-warn mt-1">
                    ⚠️ {p.max_night_run_days} noches seguidas superan el tope legal de tu
                    jurisdicción ({legalMaxNights}): sólo asignable con adscripción
                    voluntaria
                  </p>
                )}
              </div>
              <button
                onClick={() => setAssignPattern(assignPattern === p.id ? null : p.id)}
                className="px-3 py-2 bg-brand text-white rounded-lg text-sm hover:bg-brand-2"
              >
                Asignar
              </button>
            </div>

            {assignPattern === p.id && (
              <div className="mt-3 pt-3 border-t border-line space-y-2">
                <div className="flex gap-2">
                  <select
                    value={assignUserId}
                    onChange={(e) => setAssignUserId(e.target.value)}
                    className="flex-1 min-w-0 rounded-lg border-line-strong text-sm"
                  >
                    <option value="">Trabajador…</option>
                    {workers
                      .filter((w) => w.is_active)
                      .map((w) => (
                        <option key={w.id} value={w.id}>
                          {`${w.first_name} ${w.last_name}`.trim()}
                        </option>
                      ))}
                  </select>
                  <input
                    type="date"
                    value={anchorDate}
                    onChange={(e) => setAnchorDate(e.target.value)}
                    className="rounded-lg border-line-strong text-sm shrink-0"
                  />
                </div>
                {workers.length === 0 && (
                  <p className="text-xs text-warn">
                    Sin plantilla cargada: el listado de trabajadores con
                    nombres sólo lo ve un administrador. La casilla de
                    identificador a ciegas se retiró — pide la asignación a un
                    administrador.
                  </p>
                )}
                <label className="flex items-center gap-2 text-xs text-ink-2">
                  <input
                    type="checkbox"
                    checked={voluntaryNight}
                    onChange={(e) => setVoluntaryNight(e.target.checked)}
                  />
                  Adscripción voluntaria al turno de noche (exigida según la
                  jurisdicción y el convenio aplicables)
                </label>
                <button
                  onClick={assign}
                  disabled={!assignUserId || !anchorDate}
                  className="w-full py-2 bg-brand text-white rounded-lg text-sm hover:bg-brand-2 disabled:opacity-40"
                >
                  Confirmar asignación (día 0 del ciclo = fecha ancla)
                </button>
              </div>
            )}
          </div>
        ))}

        {patterns.length === 0 && !showForm && (
          <p className="text-center text-sm text-ink-3 py-10">Sin patrones aún</p>
        )}
      </div>
    </AdminLayout>
  );
}
